/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone, OnDestroy, OnInit} from '@angular/core';
import {NgForm} from '@angular/forms';
import {Subscription} from 'rxjs';
import * as _ from 'lodash';
import template from './mapping-route-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {VisitorProfileFields} from '../../components/api/resources/resource/visitor-profile-fields';
import {Uuid} from '../../components/util/uuid';
import {adnFormHelper} from '../common/controller/form-helper';
import {
  clearFormValidation,
  createFormSubmitState,
  exposeFormSubmitStateAccessors,
  FormControlError,
  FormSubmitState,
  handleApiValidationErrors,
  hasFormValidationError,
  markFormSubmitError,
  markFormSubmitSuccess,
  updateFormWarnings
} from '../common/controller/form-submit-state';
import {FieldMappingResource} from '../../components/api/resources/resource/field-mapping.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {WINDOW} from '../angular-platform-providers';

interface MappingFieldOption {
  name: string;
  displayName: string;
}

interface VmMapping {
  field: string;
  aliases: string;
  transformations: Array<{key: string; value: string}>;
  edited?: boolean;
  deleted?: boolean;
  name?: string;
}

@Component({
  standalone: false,
  selector: 'adn-mapping-route-page',
  template
})
export class MappingRoutePageComponent implements OnInit, OnDestroy {
  cancel: (form?: NgForm) => void;
  editsMade: () => void;
  saveDirect: (resource: any) => void;
  setForm: (form: NgForm) => void;
  getUnderlyingModel: () => any;

  fields: MappingFieldOption[] = [];
  isLoading = true;
  detailsPref = false;
  hasCategoryMapping = false;
  hasKeywordMapping = false;
  readonly columnOrders = ['field', 'alias', 'transformations'];

  model: any;
  submitState: FormSubmitState = createFormSubmitState();
  pending = false;

  private _registeredForm: NgForm | null = null;
  private _formSub: Subscription | null = null;
  private _formSettleTimer: any = null;
  private _initializingForm = false;
  private _angularFormDirty = false;
  private readonly formControlErrors: FormControlError[] = [];
  private underlyingModel: any;

  constructor(
    private readonly eventBus: AppEventBusService,
    @Inject(WINDOW) private readonly window: any,
    @Inject(FieldMappingResource) private readonly FieldMapping: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly visitorProfileFields: VisitorProfileFields,
    private readonly ngZone: NgZone
  ) {
    exposeFormSubmitStateAccessors(this, this.submitState, {
      getRegisteredForm: () => this._registeredForm
    });
  }

  ngOnInit(): void {
    this.visitorProfileFields.get().then((fieldList: any[]) => {
      this.ngZone.run(() => {
        this.fields = (fieldList || []).map((field) => ({
          name: field.name,
          displayName: _.lowerCase(field.name)
        }));
      });
    });

    this.FieldMapping.query().$promise.then((data: any) => {
      this.ngZone.run(() => {
        if (_.get(data, 'results.length')) {
          this.setUpResourceForm(_.head(data.results));
          return;
        }
        this.FieldMapping.create({
          id: Uuid.generate(),
          name: 'Default mapping name'
        }).savePartial().then((persistedMapping: any) => {
          this.ngZone.run(() => {
            this.setUpResourceForm(persistedMapping);
          });
        });
      });
    });
  }

  ngOnDestroy(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
  }

  get visibleMappings(): VmMapping[] {
    return (this.model?.vmMappings || []).filter((mapping: VmMapping) => !mapping.deleted);
  }

  registerForm(editForm: NgForm): string | null {
    if (this._registeredForm !== editForm) {
      this._registeredForm = editForm;
      this.setForm(editForm);
      this._startTrackingForm();
    }
    return null;
  }

  submit(editForm: NgForm): void {
    if (this.pending || this.isSaveDisabled(editForm)) {
      return;
    }

    this.pending = true;
    this.submitState.ngForm = editForm;
    clearFormValidation(this.submitState, this.formControlErrors);
    this.onBeforeSubmit();
    Object.assign(this.underlyingModel, this.model);

    this.underlyingModel.savePartial(null, 'save', null).then((resource: any) => {
      this.ngZone.run(() => {
        updateFormWarnings(this.submitState, resource || this.model, this.LocalNetworkProfile.isPureMarketplacePlatform());
        this.saveDirect(resource);
        markFormSubmitSuccess(this.submitState, editForm);
        this._angularFormDirty = false;
        this._startTrackingForm();
      });
    }, (response: any) => {
      if ((response.status === 400 || response.status === 403 || response.status === 422)
        && (response.data.type === 'ValidationResult' || response.data.type === 'ErrorMessage')) {
        handleApiValidationErrors(
          this.submitState,
          this.model,
          _.get(response, ['data', 'errors']) || response.data,
          this.formControlErrors,
          editForm
        );
      }
      markFormSubmitError(this.submitState, editForm);
    }).finally(() => {
      this.pending = false;
      this.eventBus.emit('SUBMIT_PROCESSED');
    });
  }

  private setUpResourceForm(model: any): void {
    this.underlyingModel = model;
    adnFormHelper.setUpForm(this, model, {
      afterSave: (resource: any) => {
        this.model.mappings = resource.mappings;
        this.vmMap();
      },
      afterInit: () => {
        this.vmMap();
        this.isLoading = false;
      },
      replaceCancel: (form: NgForm) => {
        this.model = _.cloneDeep(this.getUnderlyingModel());
        this.vmMap();
        this.submitState.validSubmission = false;
        this._angularFormDirty = false;
        if (form) {
          form.control.markAsPristine();
        }
      }
    });

    this.editsMade = () => {
      if (this._initializingForm) {
        return;
      }
      this._registeredForm?.control.markAsDirty();
      this._angularFormDirty = true;
    };

    this.setForm = (editForm: NgForm) => {
      this._registeredForm = editForm;
    };

    const resetModel = this.cancel;
    this.cancel = (editForm: NgForm) => {
      if (this.isFormPristine(editForm) || this.pending || (editForm.invalid && !hasFormValidationError(this.submitState))) {
        return;
      }
      resetModel(editForm);
    };
  }

  isFormPristine(editForm: NgForm): boolean {
    void editForm;
    return !this._angularFormDirty;
  }

  isSaveDisabled(editForm: NgForm): boolean {
    return this.isFormPristine(editForm)
      || this.pending
      || (editForm.invalid && !hasFormValidationError(this.submitState));
  }

  onFieldChange(): void {
    this.editsMade();
  }

  addCriteria(parentIndex: number): void {
    this.model.vmMappings[parentIndex] = this.model.vmMappings[parentIndex] || this.newEntry();
    this.model.vmMappings[parentIndex].transformations.push({key: '', value: ''});
    this.onFieldChange();
  }

  removeCriteria(parentIndex: number, index: number): void {
    _.pullAt(this.model.vmMappings[parentIndex].transformations, index);
    this.onFieldChange();
  }

  toggleEditMapping(index: number): void {
    _.forEach(this.model.vmMappings, (mapping: VmMapping, i: number) => {
      mapping.edited = i === index ? !mapping.edited : false;
    });
    this.onFieldChange();
  }

  removeMapping(mapping: VmMapping): void {
    this.onFieldChange();
    mapping.edited = false;
    mapping.deleted = true;
    this.checkMappingTypes();
  }

  addMapping(field?: string): void {
    this.onFieldChange();
    this.model.vmMappings = this.model.vmMappings || [];
    this.model.vmMappings.unshift(this.newEntry(field));
    this.checkMappingTypes();
  }

  uploadHook(): void {
    this.isLoading = true;
    this.FieldMapping.query().$promise.then((data: any) => {
      this.ngZone.run(() => {
        this.saveDirect(_.head(data.results));
        this.isLoading = false;
      });
    });
  }

  onBeforeSubmit(): void {
    this.model.mappings = this.sanitiseVm();
  }

  getRows(): any[] {
    const data = this.sanitiseVm();
    const rows: any[] = [];
    _.forEach(data, (val, key) => {
      if (key === 'KEYWORD' || key === 'CATEGORY') {
        _.forEach(val.transformations, (tVal, tKey) => {
          rows.push({field: key, alias: '', transformations: tKey + '=' + tVal});
        });
      } else {
        const mapping: {field: string; alias: string; transformations: string} = {
          field: key,
          alias: val.aliases.join(';'),
          transformations: ''
        };
        mapping.transformations = _.map(val.transformations, (tVal, tKey) => {
          return tKey + '=' + tVal;
        }).join(';');
        rows.push(mapping);
      }
    });
    return rows;
  }

  getExampleRows(): any[] {
    return [
      {field: 'company', alias: 'co;com;business', transformations: 'General Motors=GM;Alphabet=Google;AMA=Amazon'},
      {field: 'city', alias: 'cy;town;village', transformations: 'Los Angeles=LA;New York=NY;San Francisco=SF'},
      {field: 'KEYWORD', alias: '', transformations: 'golf=sport'},
      {field: 'KEYWORD', alias: '', transformations: 'tennis=sport'},
      {field: 'CATEGORY', alias: '', transformations: 'pizza=food'},
      {field: 'CATEGORY', alias: '', transformations: 'curry=food'}
    ];
  }

  downloadCsv(rows: any[], filename: string): void {
    const header = this.columnOrders.join(',');
    const body = rows.map((row) => {
      return this.columnOrders.map((column) => this.escapeCsvValue(row[column])).join(',');
    }).join('\r\n');
    const csv = '\uFEFF' + header + '\r\n' + body;
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = this.window.URL.createObjectURL(blob);
    const link = this.window.document.createElement('a');
    link.href = url;
    link.download = filename;
    this.window.document.body.appendChild(link);
    link.click();
    this.window.document.body.removeChild(link);
    this.window.URL.revokeObjectURL(url);
  }

  private vmMap(): void {
    if (!this.model) {
      return;
    }
    this.model.vmMappings = _.map(_.get(this.model, 'mappings') || [], (mapping: any, key: string) => {
      const transforms = _.map(mapping.transformations, (val, transformKey) => ({
        key: transformKey,
        value: val
      }));
      return {
        field: key,
        aliases: mapping.aliases.join(', '),
        transformations: transforms.length > 0 ? transforms : [{key: '', value: ''}]
      };
    });
    this.checkMappingTypes();
  }

  private checkMappingTypes(): void {
    this.hasCategoryMapping = !!_.find(this.model?.vmMappings, (mapping: VmMapping) => mapping.field === 'CATEGORY' && !mapping.deleted);
    this.hasKeywordMapping = !!_.find(this.model?.vmMappings, (mapping: VmMapping) => mapping.field === 'KEYWORD' && !mapping.deleted);
  }

  private newEntry(type?: string): VmMapping {
    return {
      field: type || '',
      aliases: '',
      transformations: [{key: '', value: ''}],
      edited: true
    };
  }

  private sanitiseVm(): Record<string, any> {
    const mappings: Record<string, any> = {};
    _.forEach(this.model.vmMappings, (vmMapping: VmMapping) => {
      if (!vmMapping.field) {
        return;
      }
      const transformations: Record<string, string> = {};
      _.forEach(vmMapping.transformations, (transform) => {
        if (_.trim(_.get(transform, 'key')).length > 0 && _.trim(_.get(transform, 'value')).length > 0) {
          transformations[transform.key] = transform.value;
        }
      });
      mappings[vmMapping.field] = {
        name: vmMapping.deleted ? '' : vmMapping.name,
        aliases: !vmMapping.deleted && _.trim(vmMapping.aliases).length > 0
          ? _.map(vmMapping.aliases.split(','), (val) => _.trim(val))
          : [],
        transformations: vmMapping.deleted ? {} : transformations
      };
    });
    return mappings;
  }

  private escapeCsvValue(value: any): string {
    if (_.isNil(value)) {
      return '';
    }
    const stringValue = String(value);
    if (/[",\r\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }

  private _startTrackingForm(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
    this._initializingForm = true;
    this._angularFormDirty = false;
    this._formSettleTimer = setTimeout(() => {
      if (!this._registeredForm) {
        return;
      }
      this._registeredForm.control.markAsPristine();
      this._registeredForm.control.markAsUntouched();
      this._angularFormDirty = false;
      this._initializingForm = false;
      this._formSub = this._registeredForm.valueChanges?.subscribe(() => {
        if (!this._initializingForm && !this._angularFormDirty) {
          this._angularFormDirty = true;
        }
      }) || null;
    }, 500);
  }
}
